import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

import { TbChartBubbleFilled } from "react-icons/tb";
import { FaUmbrellaBeach } from "react-icons/fa";
import { MdWaterDrop } from "react-icons/md";
import { GiCardJoker, GiJigsawBox, GiTargetShot } from "react-icons/gi";
import { SiNeteasecloudmusic } from "react-icons/si";
import { PiCloudFogFill, PiListNumbersFill } from "react-icons/pi";

import { GameId } from "../../../types/enums";
import ResetTools from "../ResetTools";
import BreathingBubble from "../ResetTools/BreathingBubble";
import ZenGarden from "../ResetTools/ZenGarden";
import FocusGrid from "../ResetTools/FocusGrid";
import WaterRipple from "../ResetTools/WaterRipple";
import FocusDot from "../ResetTools/FocusDot";
import MemoryFlip from "../ResetTools/MemoryFlip";
import PatternBuilder from "../ResetTools/PatternBuilder";
import JigsawMini from "../ResetTools/JigsawMini";
import WordBuilder from "../ResetTools/WordBuilder";

const ResetToolsSection: React.FC = () => {
  const [selectedTool, setSelectedTool] = useState<GameId | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showModal]);

  const closeModal = () => {
    setShowModal(false);
    setSelectedTool(null);
  };

  const resetTools = {
    calming: [
      {
        id: GameId.BREATHING_BUBBLE,
        name: "Breathing Bubble",
        emoji: <TbChartBubbleFilled className="text-2xl text-blue-300" />,
        instruction:
          "Follow the bubble’s rhythm. Breathe in as it grows, breathe out as it shrinks. Let your breathing slow down naturally.",
        duration: "1–2 min",
      },
      {
        id: GameId.ZEN_GARDEN,
        name: "Zen Garden",
        emoji: <FaUmbrellaBeach className="text-2xl text-blue-300" />,
        instruction:
          "Drag gently to create patterns in the sand. Move slowly and let your mind settle with each stroke.",
        duration: "2–3 min",
      },
      {
        id: GameId.WATER_RIPPLE,
        name: "Water Ripple",
        emoji: <MdWaterDrop className="text-2xl text-blue-300" />,
        instruction:
          "Tap anywhere to create soft ripples. Watch them spread and fade as you slow your thoughts.",
        duration: "1–2 min",
      },
    ],

    cognitive: [
      {
        id: GameId.FOCUS_DOT,
        name: "Focus Dot",
        emoji: <GiTargetShot className="text-2xl text-green-300" />,
        instruction:
          "Keep your attention on the moving dot. If your mind drifts, gently bring your focus back.",
        duration: "1–2 min",
      },
      {
        id: GameId.MEMORY_FLIP,
        name: "Memory Flip",
        emoji: <GiCardJoker className="text-2xl text-green-300" />,
        instruction:
          "Turn over cards and remember where each symbol appears. Move calmly and trust your memory.",
        duration: "2–3 min",
      },
      {
        id: GameId.PATTERN_BUILDER,
        name: "Pattern Builder",
        emoji: <SiNeteasecloudmusic className="text-2xl text-green-300" />,
        instruction:
          "Observe the sequence carefully, then repeat it from memory. Stay present with each step.",
        duration: "1–2 min",
      },
    ],

    puzzles: [
      {
        id: GameId.JIGSAW_MINI,
        name: "Jigsaw Mini",
        emoji: <GiJigsawBox className="text-2xl text-purple-300" />,
        instruction:
          "Move the pieces until the image comes together. Take your time and enjoy the process.",
        duration: "2–3 min",
      },
      {
        id: GameId.FocusGrid,
        name: "Focus Grid",
        emoji: <PiListNumbersFill className="text-2xl text-purple-300" />,
        instruction:
          "Fill the grid so numbers don’t repeat in any row or column. Work through it one step at a time.",
        duration: "3–4 min",
      },
      {
        id: GameId.WORD_CLOUD,
        name: "Word Builder",
        emoji: <PiCloudFogFill className="text-2xl text-purple-300" />,
        instruction:
          "Form meaningful words from the letters provided. Let your mind explore calmly.",
        duration: "2–3 min",
      },
    ],
  };

  const getTool = (id: GameId) =>
    Object.values(resetTools).flat().find((t) => t.id === id);

  return (
    <>
      <ResetTools
        resetTools={resetTools}
        onGameSelect={(id) => {
          setSelectedTool(id);
          setShowModal(true);
        }}
      />

      {showModal && selectedTool &&
        createPortal(
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-xl flex flex-col overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="text-xl">
                    {getTool(selectedTool)?.emoji}
                  </div>

                  <div>
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {getTool(selectedTool)?.name}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {getTool(selectedTool)?.duration}
                    </p>
                  </div>
                </div>

                <button
                  onClick={closeModal}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-sm"
                >
                  ✕
                </button>
              </div>

              {/* Instruction */}
              <div className="px-5 py-4 bg-orange-50 border-b border-orange-100">
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  {getTool(selectedTool)?.instruction}
                </p>
              </div>

              {/* Tool Workspace */}
              <div className="flex-1 overflow-auto pt-6 px-5 pb-5 flex justify-center bg-gradient-to-b from-white to-orange-50/30">
                <div className="relative w-full max-w-2xl">
                  {/* subtle focus glow */}
                  <div className="absolute inset-0 bg-orange-200/20 blur-2xl rounded-full scale-105 pointer-events-none"></div>
                  <div className="relative">
                    {selectedTool === GameId.BREATHING_BUBBLE && <BreathingBubble />}
                    {selectedTool === GameId.ZEN_GARDEN && <ZenGarden />}
                    {selectedTool === GameId.WATER_RIPPLE && <WaterRipple />}

                    {selectedTool === GameId.FOCUS_DOT && <FocusDot />}
                    {selectedTool === GameId.MEMORY_FLIP && <MemoryFlip />}
                    {selectedTool === GameId.PATTERN_BUILDER && <PatternBuilder />}

                    {selectedTool === GameId.FocusGrid && <FocusGrid />}
                    {selectedTool === GameId.JIGSAW_MINI && <JigsawMini />}
                    {selectedTool === GameId.WORD_CLOUD && <WordBuilder />}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ResetToolsSection;